/**
 * The Mayor's Desk — the morning espresso.
 *
 * One click, one screen, most important first. It is not another brief: the
 * brief is the forwardable text about four conditions, and this is the
 * ranked morning read — the state of the town, and what the world's
 * publishers are saying about it, in Thai, with the original kept beside
 * every translation.
 *
 * Pure: it reshapes the desk feed the API already fetched, so every figure
 * keeps its source and its reference time, and a gap is reported rather than
 * padded away.
 */

import type { DeskLang, DeskStory, FallbackTier } from "@chonburi/shared";
import { LEVEL_LABEL, type BriefEvidenceLens, type BriefLevel, type BriefTile } from "./mayorBrief";

/** The languages the desk covers, named in Thai and English. */
export const LANG_LABEL: Record<DeskLang, { th: string; en: string; short: string }> = {
  th: { th: "ไทย", en: "Thai", short: "TH" },
  en: { th: "อังกฤษ", en: "English", short: "EN" },
  ja: { th: "ญี่ปุ่น", en: "Japanese", short: "JA" },
  zh: { th: "จีน", en: "Chinese", short: "ZH" },
  ko: { th: "เกาหลี", en: "Korean", short: "KO" },
  de: { th: "เยอรมัน", en: "German", short: "DE" },
  fr: { th: "ฝรั่งเศส", en: "French", short: "FR" },
  other: { th: "ภาษาอื่น", en: "Other", short: "··" },
};

/**
 * The languages the mayor reads without a translation.
 *
 * A story in one of these stays on the desk in the publisher's own words when
 * no Thai rendering could be produced. A story in any other language does
 * not: it would be a wall of kanji on a Thai morning, so it is dropped and
 * the gap is counted.
 */
const READABLE: DeskLang[] = ["th", "en"];

/** How many headlines each compartment holds. */
export const DESK_LIMITS = {
  /** The one thing to read first. */
  lead: 1,
  /**
   * Coverage published in a language the mayor does not read.
   *
   * Three, not four: the desk is a morning read and has to fit one screen,
   * and a fourth foreign headline pushes the whole bento past the fold. The
   * ones that are left are named in the census.
   */
  world: 3,
  /** Coverage in Thai and English. */
  coverage: 3,
  /** From any one language, so a prolific desk cannot fill the compartment. */
  perLanguage: 2,
} as const;

/**
 * What a story must have to raise its own weight.
 *
 * An emergency outranks a trade story: the mayor acts on the first and reads
 * the second. The tags come from the news adapter, which already classifies
 * each headline by the beat it belongs to.
 */
const ACTION_WEIGHT: Record<string, number> = {
  EM: 26, // flood, fire, accident, evacuation
  FU: 20, // power cut, water shortage
  PU: 14, // health
  PO: 12, // protest, demonstration
  EN: 6, // economy, investment, trade
};

/**
 * A story's weight falls off over a month and stops counting after three.
 *
 * A municipality this size is written about in other languages rarely, and
 * when it is, the report lands weeks after the event — the German trade press
 * reported AIDA leaving Laem Chabang ten days after it happened. Age is
 * therefore discounted, not disqualifying.
 */
function freshness(publishedAt: string, now: Date): number {
  const age = now.getTime() - Date.parse(publishedAt);
  if (!Number.isFinite(age)) return 0;
  const days = age / 86_400_000;
  if (days < 0) return 30;
  return Math.max(0, 30 - days);
}

/**
 * How much a story matters to the mayor of Chonburi.
 *
 * Three things decide it, and all three are visible on the desk itself: how
 * close the story is to the municipality (the adapter's relevance score),
 * whether it is about something the mayor has to act on, and how fresh it is.
 */
export function deskPriority(story: DeskStory, now: Date): number {
  const action = story.tags.reduce((sum, t) => sum + (ACTION_WEIGHT[t] ?? 0), 0);
  return story.score + action + freshness(story.publishedAt, now);
}

/** One headline on the desk. */
export interface DeskItem {
  id: string;
  /** The headline the mayor reads: the Thai rendering, or the publisher's own words. */
  headline: string;
  /** The same headline in English, for the English text. */
  headlineEn: string;
  /** The publisher's own words, when they differ from the Thai headline. */
  original: string | null;
  /** The publisher's own words, when they differ from the English rendering. */
  originalEn: string | null;
  /**
   * How the English rendering was produced.
   *
   * The English text is a document in English, so a Thai headline has to be
   * translated to appear in it, and a headline that could not be translated
   * says so rather than passing for English.
   */
  enTranslation: "source" | "machine" | "untranslated";
  /** The language the publisher wrote in. */
  lang: DeskLang;
  /** True when the headline the mayor reads is a machine translation. */
  machine: boolean;
  source: string;
  sourceUrl: string;
  publishedAt: string;
  /** A known place inside the municipality, when the story names one. */
  place: string | null;
  tags: string[];
  /** How much this matters — higher first. */
  priority: number;
}

/**
 * Turn a feed story into a desk item.
 *
 * One rule, no special cases: the Thai rendering when there is one, the
 * publisher's own words when there is not. An English headline needs no
 * translation to be readable, so it stays as it is and is not marked as one.
 */
export function toDeskItem(story: DeskStory, now: Date): DeskItem {
  const headline = story.titleTh || story.title;
  const headlineEn = story.titleEn || story.title;
  return {
    id: story.id,
    headline,
    headlineEn,
    original: headline === story.title ? null : story.title,
    originalEn: headlineEn === story.title ? null : story.title,
    enTranslation: !story.titleEn
      ? "untranslated"
      : headlineEn === story.title
        ? "source"
        : "machine",
    lang: story.lang,
    machine: story.translation === "machine",
    source: story.source,
    sourceUrl: story.sourceUrl,
    publishedAt: story.publishedAt,
    place: story.place,
    tags: story.tags,
    priority: deskPriority(story, now),
  };
}

/** A gap the desk has to name rather than hide. */
export interface DeskGap {
  th: string;
  en: string;
}

/** The desk feed's own state, as the desk sees it. */
export interface DeskFeedState {
  tier: FallbackTier | "loading";
  ageMinutes: number;
  source: string;
  /** The languages the feed drew from. */
  languages: DeskLang[];
  /** A language whose feed answered but found nothing about the municipality. */
  emptyLanguages: DeskLang[];
  /** A language whose feed did not answer at all. */
  failedLanguages: DeskLang[];
  /** A language whose stories exist but could not be rendered in Thai. */
  untranslatedLanguages: DeskLang[];
  /** Stories that exist but could not be rendered in Thai. */
  unreadable: number;
  /** True when at least one headline was machine-translated. */
  translated: boolean;
}

export interface MayorDesk {
  /** The single most important thing this morning. */
  lead: DeskItem | null;
  /** Coverage published in a language the mayor does not read. */
  world: DeskItem[];
  /** Coverage in Thai and English. */
  coverage: DeskItem[];
  /** Everything the desk could not show. */
  gaps: DeskGap[];
  /** Every headline on the desk, most important first. */
  items: DeskItem[];
}

/**
 * Everything the desk could not show.
 *
 * A desk that looks full while hiding a gap is the one thing this surface
 * may not do, so an empty language and an untranslated headline are both
 * named — and an empty language is never padded with whatever the query
 * happened to return.
 */
export function deskGaps(feed: DeskFeedState | null): DeskGap[] {
  if (!feed) {
    return [
      {
        th: "ยังไม่ได้ดึงข่าว — โต๊ะนี้จึงแสดงเฉพาะสภาพของเมือง",
        en: "The news desk has not loaded — this desk shows the municipality's conditions only",
      },
    ];
  }

  const gaps: DeskGap[] = [];
  if (feed.tier === "unavailable") {
    gaps.push({ th: "แหล่งข่าวไม่ตอบสนอง", en: "The news source did not answer" });
  }
  if (feed.failedLanguages.length) {
    // A source that did not answer is named as broken, in its own name.
    // Reporting it as a language with no coverage would tell the mayor
    // there is no news about Chonburi in that language, when the truth is
    // that nobody looked.
    const names = feed.failedLanguages.map((l) => LANG_LABEL[l].th).join(" · ");
    const namesEn = feed.failedLanguages.map((l) => LANG_LABEL[l].en).join(", ");
    const n = feed.failedLanguages.length;
    gaps.push({
      th: `${n} ภาษาไม่ตอบสนอง (${names}) — ไม่ได้ตรวจสอบ`,
      en: `${n} ${n === 1 ? "language feed did" : "language feeds did"} not answer (${namesEn}) — not checked`,
    });
  }
  if (feed.emptyLanguages.length) {
    const names = feed.emptyLanguages.map((l) => LANG_LABEL[l].th).join(" · ");
    const namesEn = feed.emptyLanguages.map((l) => LANG_LABEL[l].en).join(", ");
    const n = feed.emptyLanguages.length;
    gaps.push({
      th: `${n} ภาษาไม่มีข่าวเกี่ยวกับเมือง (${names})`,
      en: `${n} ${n === 1 ? "language has" : "languages have"} no coverage about the municipality (${namesEn})`,
    });
  }
  if (feed.unreadable > 0) {
    // Each language is named in its own line's language. A gap written in
    // Thai that names the language in Thai is unreadable to the reader of
    // the English line — and the English line is the one a partner abroad
    // ever sees.
    const namesTh = feed.untranslatedLanguages.map((l) => LANG_LABEL[l].th).join(" · ");
    const namesEn = feed.untranslatedLanguages.map((l) => LANG_LABEL[l].en).join(", ");
    gaps.push({
      th: `${feed.unreadable} พาดหัวไม่ได้แปลเป็นไทย${namesTh ? ` (${namesTh})` : ""} — ไม่ได้แสดงบนโต๊ะนี้`,
      en: `${feed.unreadable} ${feed.unreadable === 1 ? "headline" : "headlines"} not translated into Thai${namesEn ? ` (${namesEn})` : ""} — not shown on this desk`,
    });
  }
  return gaps;
}

/**
 * Build the desk.
 *
 * The lead is the most important headline of all, whatever language it is
 * in. The rest are split by kind, because a mayor reads them differently:
 * the foreign coverage is the part he would never have found himself, and
 * the Thai and English coverage is the part he would have read anyway.
 */
export function buildDesk(stories: DeskStory[], feed: DeskFeedState | null, now: Date): MayorDesk {
  const ranked = stories
    // A story the mayor cannot read is not a desk item. The API already
    // drops them, but the browser can be handed one it could not render,
    // and a wall of kanji on a Thai morning is worse than a named gap.
    .filter((s) => s.titleTh.length > 0 || READABLE.includes(s.lang))
    .map((s) => toDeskItem(s, now))
    .sort((a, b) => b.priority - a.priority || b.publishedAt.localeCompare(a.publishedAt));

  const lead = ranked[0] ?? null;
  const rest = ranked.slice(DESK_LIMITS.lead);

  const world: DeskItem[] = [];
  const perLanguage = new Map<DeskLang, number>();
  for (const item of rest) {
    if (world.length >= DESK_LIMITS.world) break;
    if (READABLE.includes(item.lang)) continue;
    const count = perLanguage.get(item.lang) ?? 0;
    if (count >= DESK_LIMITS.perLanguage) continue;
    perLanguage.set(item.lang, count + 1);
    world.push(item);
  }

  const coverage = rest
    .filter((i) => READABLE.includes(i.lang))
    .slice(0, DESK_LIMITS.coverage);

  return {
    lead,
    world,
    coverage,
    gaps: deskGaps(feed),
    items: lead ? [lead, ...world, ...coverage] : [...world, ...coverage],
  };
}

/** One condition, as the state strip holds it. */
export interface DeskStateCell {
  key: string;
  /** The short name the strip prints — the brief keeps the long ones. */
  titleTh: string;
  titleEn: string;
  /** The full subject name, for the tooltip. */
  fullTh: string;
  fullEn: string;
  level: BriefLevel;
  /** The chip's word — the subject and the severity, never a bare severity. */
  labelTh: string;
  labelEn: string;
  /** The one figure the cell can hold. */
  figureTh: string | null;
  figureEn: string | null;
  source: string;
  observedAt: string | null;
  evidence: BriefEvidenceLens;
}

/**
 * The short name each condition carries in the strip.
 *
 * The strip is a comparison of four states, so each cell gets a name that
 * fits on one line beside its own level word. The full name stays on the
 * cell's tooltip and on the brief, which is where the long title belongs.
 */
const STATE_SHORT: Record<string, { th: string; en: string }> = {
  air: { th: "อากาศ", en: "Air" },
  rain: { th: "ฝน · น้ำ", en: "Rain · water" },
  sea: { th: "ทะเล", en: "Sea" },
  incidents: { th: "เหตุการณ์", en: "Incidents" },
};

/**
 * Project the brief's tiles onto the desk's state strip.
 *
 * The strip is a comparison of four states, so it keeps each tile's own
 * level word, one figure, and the source and reference time under it. It
 * never borrows the brief's sentence: that sentence carries every
 * disclosure and would fill a cell the mayor cannot read at a glance.
 */
export function deskState(tiles: BriefTile[]): DeskStateCell[] {
  return tiles.map((tile) => {
    const label = tile.levelLabel ?? LEVEL_LABEL[tile.level];
    const short = STATE_SHORT[tile.key] ?? { th: tile.titleTh, en: tile.titleEn };
    return {
      key: tile.key,
      titleTh: short.th,
      titleEn: short.en,
      fullTh: tile.titleTh,
      fullEn: tile.titleEn,
      level: tile.level,
      labelTh: label.th,
      labelEn: label.en,
      figureTh: tile.figure?.th ?? null,
      figureEn: tile.figure?.en ?? null,
      source: tile.source,
      observedAt: tile.observedAt,
      evidence: tile.evidence,
    };
  });
}

/**
 * What a story is about, in words.
 *
 * The tags are codes the news adapter uses; a mayor reads none of them. The
 * lead carries the beat in words, because "this is about a flood" is the
 * difference between a headline he reads and one he skips.
 */
const BEAT_WORD: Record<string, { th: string; en: string }> = {
  EM: { th: "เหตุฉุกเฉิน", en: "emergency" },
  PU: { th: "สาธารณสุข", en: "health" },
  PO: { th: "การประท้วง", en: "protest" },
  FU: { th: "พลังงานและน้ำ", en: "power and water" },
  EN: { th: "เศรษฐกิจ", en: "economy" },
};

/** How long ago a story was published, in words. */
export function agoLabel(publishedAt: string, now: Date): { th: string; en: string } {
  const ms = now.getTime() - Date.parse(publishedAt);
  if (!Number.isFinite(ms)) return { th: "ไม่ทราบเวลา", en: "time unknown" };
  const hours = Math.round(ms / 3_600_000);
  if (hours < 1) return { th: "เมื่อสักครู่", en: "just now" };
  if (hours < 24) return { th: `${hours} ชั่วโมงที่แล้ว`, en: `${hours} h ago` };
  const days = Math.round(hours / 24);
  return { th: `${days} วันที่แล้ว`, en: `${days} d ago` };
}

/** The place, the beat and the age of a story — the line under the lead. */
export function deskContext(item: DeskItem, now: Date): { th: string; en: string } {
  const ago = agoLabel(item.publishedAt, now);
  const th: string[] = [];
  const en: string[] = [];
  if (item.place) {
    th.push(item.place);
    en.push(item.place);
  }
  for (const tag of item.tags) {
    const beat = BEAT_WORD[tag];
    if (!beat) continue;
    th.push(beat.th);
    en.push(beat.en);
  }
  th.push(ago.th);
  en.push(ago.en);
  return { th: th.join(" · "), en: en.join(" · ") };
}

/** The desk as text, ready to forward. */
export interface DeskText {
  th: string;
  en: string;
}

/** "25 ต.ค. 2569" — the publication date of a story, in Thai. */
function thaiStamp(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "—";
  const d = new Date(t);
  const months = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear() + 543}`;
}

/**
 * "25 Oct 2026" — the same date, for the English text.
 *
 * The English text is a document for a reader who does not read Thai, so
 * it carries the Gregorian date rather than the Thai one. It is the same
 * day; only the calendar it is written in changes.
 */
function englishStamp(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "—";
  const d = new Date(t);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/**
 * The desk as text.
 *
 * Thai is written first, because that is the language it is forwarded in.
 * A machine-translated headline is marked as one and the publisher's own
 * words follow it, so a mistranslated number is caught before it is
 * forwarded rather than after.
 */
export function deskText(desk: MayorDesk, tiles: BriefTile[], now: Date): DeskText {
  const stateLine = tiles
    .map((t) => {
      const label = t.levelLabel ?? LEVEL_LABEL[t.level];
      return `${t.titleTh} ${label.th}`;
    })
    .join(" · ");

  const stateLineEn = tiles
    .map((t) => {
      const label = t.levelLabel ?? LEVEL_LABEL[t.level];
      return `${t.titleEn} ${label.en}`;
    })
    .join(" · ");

  const thLines: string[] = [`โต๊ะนายกเมืองชลบุรี ${thaiStamp(now.toISOString())}`];
  const enLines: string[] = [`Chonburi Mayor's Desk ${englishStamp(now.toISOString())}`];

  if (stateLine) {
    thLines.push(`สภาพ: ${stateLine}`);
    enLines.push(`Conditions: ${stateLineEn}`);
  }

  const item = (i: DeskItem) => {
    const lang = LANG_LABEL[i.lang];
    const mark = i.machine ? ` [แปลอัตโนมัติจาก${lang.th}]` : "";
    const orig = i.original ? `\n  ต้นฉบับ: ${i.original}` : "";
    return `• ${i.headline}${mark}\n  ${i.source} · ${thaiStamp(i.publishedAt)}${orig}`;
  };
  const itemEn = (i: DeskItem) => {
    const lang = LANG_LABEL[i.lang];
    // The English text is English: the headline is the English rendering,
    // and a headline that could not be rendered in English says so rather
    // than passing for English.
    const mark =
      i.enTranslation === "machine"
        ? ` [machine-translated from ${lang.en}]`
        : i.enTranslation === "untranslated"
          ? ` [not translated into English — the publisher's words stand in]`
          : "";
    const orig = i.originalEn ? `\n  original: ${i.originalEn}` : "";
    return `• ${i.headlineEn}${mark}\n  ${i.source} · ${englishStamp(i.publishedAt)}${orig}`;
  };

  if (desk.lead) {
    thLines.push("ข่าวเด่น:");
    thLines.push(item(desk.lead));
    enLines.push("Lead story:");
    enLines.push(itemEn(desk.lead));
  }
  if (desk.world.length) {
    thLines.push("ข่าวจากภาษาอื่น:");
    desk.world.forEach((i) => thLines.push(item(i)));
    enLines.push("Coverage in other languages:");
    desk.world.forEach((i) => enLines.push(itemEn(i)));
  }
  if (desk.coverage.length) {
    thLines.push("ข่าวไทยและอังกฤษ:");
    desk.coverage.forEach((i) => thLines.push(item(i)));
    enLines.push("Thai and English coverage:");
    desk.coverage.forEach((i) => enLines.push(itemEn(i)));
  }
  if (desk.gaps.length) {
    thLines.push("สิ่งที่โต๊ะนี้แสดงไม่ได้:");
    desk.gaps.forEach((g) => thLines.push(`• ${g.th}`));
    enLines.push("What this desk could not show:");
    desk.gaps.forEach((g) => enLines.push(`• ${g.en}`));
  }

  return { th: thLines.join("\n"), en: enLines.join("\n") };
}
